import { useEffect, useLayoutEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

interface ModalFocusOptions {
  focusKey?: string;
  onEscape?: () => void;
}

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => {
      const style = window.getComputedStyle(element);
      return !element.hidden &&
        element.getAttribute("aria-hidden") !== "true" &&
        style.display !== "none" &&
        style.visibility !== "hidden";
    },
  );
}

/**
 * Gives modal overlays predictable keyboard behaviour without coupling them to
 * any one visual treatment. The overlay itself receives initial focus so its
 * accessible name and description are announced before an irreversible choice.
 */
export function useModalFocus<T extends HTMLElement>({
  focusKey = "modal",
  onEscape,
}: ModalFocusOptions = {}) {
  const modalRef = useRef<T>(null);
  const escapeHandler = useRef(onEscape);

  useEffect(() => {
    escapeHandler.current = onEscape;
  }, [onEscape]);

  useLayoutEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    const activeModal: T = modal;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const siblings = Array.from(modal.parentElement?.children ?? [])
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== modal)
      .map((element) => ({
        element,
        inert: element.inert,
        ariaHidden: element.getAttribute("aria-hidden"),
      }));

    modal.focus({ preventScroll: true });
    for (const record of siblings) {
      record.element.inert = true;
      record.element.setAttribute("aria-hidden", "true");
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && escapeHandler.current) {
        event.preventDefault();
        escapeHandler.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = focusableElements(activeModal);
      if (focusable.length === 0) {
        event.preventDefault();
        activeModal.focus({ preventScroll: true });
        return;
      }

      const first = focusable.at(0)!;
      const last = focusable.at(-1)!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !activeModal.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      for (const record of siblings) {
        record.element.inert = record.inert;
        if (record.ariaHidden === null) record.element.removeAttribute("aria-hidden");
        else record.element.setAttribute("aria-hidden", record.ariaHidden);
      }

      const fallback = document.querySelector<HTMLElement>(".game-nav button.active");
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
      else fallback?.focus({ preventScroll: true });
    };
  }, []);

  useLayoutEffect(() => {
    modalRef.current?.focus({ preventScroll: true });
  }, [focusKey]);

  return modalRef;
}
